import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Box, Typography } from '@mui/material';
import { CalendarMonth } from '@mui/icons-material';
import styled from '@emotion/styled';
import BaseWidget from './BaseWidget';
import AccountTabCalendar from '../components/AccountTabCalendar';
import { useFetchQueue } from '../hooks/useFetchQueue';
import { requestUserAssignments } from '../redux/sagas/requests/users';
import { isResponsible } from '../utils/assignmentUtils';

const EmptyState = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 160,
    color: theme.palette.text.secondary,
}));

const EmptyStateIcon = styled(CalendarMonth)(({ theme }) => ({
    fontSize: 40,
    marginBottom: theme.spacing(1),
    opacity: 0.5,
}));

const MyCalendarWidget = ({ widgetId, onRemove }) => {
    const userId = useSelector((state) => state.auth.userId);
    const currentUser = useSelector((state) => state.auth.currentUser);

    const { data: assignments, isLoading, error } = useFetchQueue(widgetId, {
        apiCall: () => requestUserAssignments(),
        cacheKey: `user-assignments-${userId}`,
        enabled: !!userId,
        transform: (response) => response,
    });

    const items = useMemo(() => {
        const list = Array.isArray(assignments) ? assignments : [];
        const fullName = currentUser?.fullName ||
            `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim();
        const responsible = fullName ? { _id: currentUser?._id, fullName, avatar: currentUser?.avatar } : undefined;
        return list
            .filter((a) => isResponsible(a) && a.startDate && a.dueDate)
            .map((a) => ({
                _id: `${a.type || 'item'}-${a._id}`,
                name: a.name,
                startDate: a.startDate,
                dueDate: a.dueDate,
                status: a.status,
                account: a.account ? { name: a.account.name, slug: a.account.slug } : undefined,
                responsible,
                _raw: a,
            }));
    }, [assignments, currentUser]);

    const getLink = (item) => {
        const raw = item._raw;
        if (!raw) return null;
        const slug = raw.account?.slug || raw.account?.name;
        if (!slug) return null;
        if (raw.type === 'Task') {
            return raw.project?._id ? `/account/${slug}/project/${raw.project._id}` : null;
        }
        return `/account/${slug}/project/${raw._id}`;
    };

    const hasItems = items.length > 0;

    return (
        <BaseWidget
            title="My Calendar"
            widgetId={widgetId}
            onRemove={onRemove}
            isLoading={isLoading}
            error={error}
        >
            {!hasItems && !isLoading ? (
                <EmptyState>
                    <EmptyStateIcon />
                    <Typography variant="body2">No assignments scheduled</Typography>
                </EmptyState>
            ) : (
                <AccountTabCalendar
                    projects={items}
                    getLink={getLink}
                    emptyMessage="No assignments in this date range"
                    stickyHeader
                />
            )}
        </BaseWidget>
    );
};

export default MyCalendarWidget;
